import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Eye, Clock, GripVertical } from 'lucide-react'
import { useAuthStore } from '@/shared/stores/auth.store'
import { systemAdminApi } from '@/features/system-admin/api/system-admin.api'

/** mm:ss remaining until `iso`, or null if no/expired deadline. */
function useCountdown(iso?: string): number | null {
  const [remaining, setRemaining] = useState<number | null>(() =>
    iso ? Math.max(0, new Date(iso).getTime() - Date.now()) : null,
  )
  useEffect(() => {
    if (!iso) {
      setRemaining(null)
      return
    }
    const tick = () => setRemaining(Math.max(0, new Date(iso).getTime() - Date.now()))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [iso])
  return remaining
}

const PILL_W = 236
const MARGIN = 16

export function ImpersonationBanner() {
  const user = useAuthStore((s) => s.user)

  // Position: null = default (bottom-right). Once dragged, {left, top} in px.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const endedRef = useRef(false)
  const remaining = useCountdown(user?.impersonation_expires_at)

  // End impersonation + FULL page refresh. A hard reload re-bootstraps auth as
  // the admin (perms, PHI transport key, route access) with zero stale state —
  // simpler and safer than patching the in-memory session back to the admin.
  const endAndReload = async () => {
    if (endedRef.current) return
    endedRef.current = true
    try {
      // Race against a timeout so a hung/slow end call can NEVER strand us on the
      // impersonated view — the reload always fires. If the end didn't process
      // (stamps not cleared), the reloaded page re-shows the banner at 0:00 and
      // this retries (endedRef resets on the fresh page).
      await Promise.race([
        systemAdminApi.endImpersonation(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ])
    } catch {
      /* reload anyway — the cap/next request reverts server-side */
    }
    window.location.href = '/'
  }

  // Auto-end when the cap countdown hits zero.
  useEffect(() => {
    if (user?.is_impersonating && remaining !== null && remaining <= 0) {
      void endAndReload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, user?.is_impersonating])

  if (!user?.is_impersonating) return null

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    // The banner is portaled into document.body (OUTSIDE #root's `zoom`
    // subtree), so every coordinate here shares one space: e.clientX/Y,
    // getBoundingClientRect(), and style.left/top are all viewport px. (Inside
    // #root the zoom would scale style.left/top only, breaking the drag.)
    const rect = rootRef.current!.getBoundingClientRect()
    const dx = e.clientX - rect.left
    const dy = e.clientY - rect.top
    const w = rect.width
    const h = rect.height
    // Pin to current left/top so the right/bottom→left/top hand-off is invisible.
    setPos({ left: rect.left, top: rect.top })
    setDragging(true)

    const move = (ev: PointerEvent) => {
      setPos({
        left: Math.min(Math.max(MARGIN, ev.clientX - dx), window.innerWidth - w - MARGIN),
        top: Math.min(Math.max(MARGIN, ev.clientY - dy), window.innerHeight - h - MARGIN),
      })
    }
    const up = () => {
      setDragging(false)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const roleName = user.roles[0]?.display_name ?? user.roles[0]?.name ?? 'User'
  const urgent = remaining != null && remaining <= 120_000
  const mmss =
    remaining == null
      ? null
      : `${Math.floor(remaining / 60_000)}:${String(Math.floor((remaining % 60_000) / 1000)).padStart(2, '0')}`

  return createPortal(
    <>
      {/* Full-viewport blue ring + faint tint — never blocks interaction. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          pointerEvents: 'none',
          border: '3px solid rgba(37, 99, 235, 0.5)',
          boxShadow: 'inset 0 0 140px rgba(37, 99, 235, 0.12)',
          background: 'rgba(37, 99, 235, 0.035)',
        }}
      />

      {/* Draggable status pill. */}
      <div
        ref={rootRef}
        style={{
          position: 'fixed',
          zIndex: 9999,
          width: PILL_W,
          ...(pos
            ? { left: pos.left, top: pos.top }
            : { right: MARGIN, bottom: MARGIN }),
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 12px',
          background: '#7f1d1d',
          color: '#fee2e2',
          borderRadius: 'var(--r-lg, 10px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            onPointerDown={onPointerDown}
            style={{ cursor: dragging ? 'grabbing' : 'grab', display: 'flex', touchAction: 'none', color: '#fca5a5' }}
            title="Drag to move"
          >
            <GripVertical size={15} />
          </span>
          <Eye size={14} />
          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Viewing as {user.first_name} {user.last_name} ({roleName})
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {mmss && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontVariantNumeric: 'tabular-nums',
                color: urgent ? '#fecaca' : '#fca5a5',
                fontWeight: 700,
              }}
            >
              <Clock size={13} />
              Ends in {mmss}
            </span>
          )}
          <button
            onClick={() => void endAndReload()}
            style={{
              marginLeft: 'auto',
              padding: '4px 14px',
              fontSize: 12,
              fontWeight: 700,
              background: '#fee2e2',
              color: '#7f1d1d',
              border: 'none',
              borderRadius: 'var(--r-md, 6px)',
              cursor: 'pointer',
            }}
          >
            Exit
          </button>
        </div>
      </div>
    </>,
    document.body,
  )
}
